import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
  ArrowLeft,
  MapPin,
  CalendarDays,
  Users,
  User,
  Phone,
  Mail,
  MessageSquareMore,
  CarFront,
} from "lucide-react";
import "./BookingPage.css";
import { Helmet } from "react-helmet-async";

type TripType = "oneWay" | "roundTrip";

type AirportOption = {
  value: string;
  label: string;
};

type QuickBookingData = {
  pickup?: string;
  destination?: string;
  tripDate?: string;
  returnDate?: string;
  passengers?: string;
  tripType?: TripType;
};

type VehicleOption = {
  value: string;
  title: string;
  meta: string;
  image: string;
};

const API_BASE_URL = process.env.REACT_APP_API_URL;

if (!API_BASE_URL) {
  throw new Error("REACT_APP_API_URL is not defined");
}

export interface BookingPageData {
  pickup: string;
  pickupLabel: string;
  destination: string;
  tripType: TripType;
  tripDate: Date | null;
  returnDate: string;
  returnTime: string;
  passengers: number;
  adults: number;
  children: number;
  babies: number;
  time: string;
  resort: string;
  cabinLuggage: number;
  checkedLuggage: number;
  specialLuggage: string;
  vehicleType: string;
  leadName: string;
  email: string;
  mobile: string;
  country: string;
  foundUs: string;
  specialRequests: string;
}

export function BookingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const quickBookingData = location.state?.quickBookingData as
    | QuickBookingData
    | undefined;

  const [tripType, setTripType] = useState<TripType>("oneWay");
  const [tripDate, setTripDate] = useState<Date | null>(null);
  const [customCountry, setCustomCountry] = useState("");

  const [formData, setFormData] = useState({
    pickup: "",
    destination: "",
    returnDate: "",
    returnTime: "",
    arrivalTime: "",

    adults: "1",
    children: "0",
    babies: "0",

    resort: "",

    cabinLuggage: "0",
    checkedLuggage: "0",
    specialLuggage: "",

    vehicleType: "",

    leadName: "",
    email: "",
    mobile: "",
    country: "",
    foundUs: "",

    specialRequests: "",
  });

  const [airports, setAirports] = useState<AirportOption[]>([]);
  const [allDestinations, setAllDestinations] = useState<string[]>([]);

  const [airportsLoading, setAirportsLoading] = useState(true);
  const [airportsError, setAirportsError] = useState("");

  const countries = [
    "Portugal [+351]",
    "United Kingdom [+44]",
    "Spain [+34]",
    "France [+33]",
    "Germany [+49]",
    "Ireland [+353]",
    "Netherlands [+31]",
    "Belgium [+32]",
    "Switzerland [+41]",
    "Italy [+39]",
    "Austria [+43]",
    "Denmark [+45]",
    "Sweden [+46]",
    "Norway [+47]",
    "Finland [+358]",
    "Poland [+48]",
    "Czech Republic [+420]",
    "Luxembourg [+352]",
    "United States [+1]",
    "Canada [+1]",
    "Brazil [+55]",
    "Australia [+61]",
    "New Zealand [+64]",
    "South Africa [+27]",
    "Other",
  ];

  const sources = [
    { value: "Google", label: t("booking.sources.google") },
    { value: "Instagram", label: t("booking.sources.instagram") },
    { value: "Facebook", label: t("booking.sources.facebook") },
    { value: "Hotel / Resort", label: t("booking.sources.hotel") },
    { value: "Recommendation", label: t("booking.sources.recommendation") },
    { value: "Returning Customer", label: t("booking.sources.returningCustomer") },
    { value: "Other", label: t("booking.common.other") },
  ];

  const vehicles: VehicleOption[] = [
    {
      value: "standard",
      title: t("booking.vehicles.standard.title"),
      meta: t("booking.vehicles.standard.meta"),
      image: "/vehicles/sedan.png",
    },
    {
      value: "group",
      title: t("booking.vehicles.group.title"),
      meta: t("booking.vehicles.group.meta"),
      image: "/vehicles/sedanplus.png",
    },
    {
      value: "premium-group",
      title: t("booking.vehicles.premiumGroup.title"),
      meta: t("booking.vehicles.premiumGroup.meta"),
      image: "/vehicles/minivan.png",
    },
  ];

  const totalPassengers =
    Number(formData.adults || 0) +
    Number(formData.children || 0) +
    Number(formData.babies || 0);

  const getVehicleCapacity = (vehicleType: string) => {
    if (vehicleType === "standard") return 4;
    if (vehicleType === "group") return 6;
    if (vehicleType === "premium-group") return 8;
    return 0;
  };

  const isVehicleDisabled = (vehicleType: string) =>
    totalPassengers > getVehicleCapacity(vehicleType);

  const passengersError =
    totalPassengers > 8 ? t("booking.errors.maxPassengers") : "";

  const selectedPickupLabel = useMemo(() => {
    const airport = airports.find((a) => a.value === formData.pickup);
    return airport?.label ?? formData.pickup;
  }, [airports, formData.pickup]);

  useEffect(() => {
    if (!quickBookingData) return;

    setTripType(quickBookingData.tripType ?? "oneWay");

    if (quickBookingData.tripDate) {
      setTripDate(new Date(`${quickBookingData.tripDate}T00:00:00`));
    }

    setFormData((prev) => ({
      ...prev,
      pickup: quickBookingData.pickup ?? prev.pickup,
      destination: quickBookingData.destination ?? prev.destination,
      returnDate: quickBookingData.returnDate ?? prev.returnDate,
      adults: quickBookingData.passengers ?? prev.adults,
    }));
  }, [quickBookingData]);

  useEffect(() => {
    let cancelled = false;

    async function loadInitialData() {
      try {
        setAirportsLoading(true);
        setAirportsError("");

        const airportsRes = await fetch(`${API_BASE_URL}/api/airports`);

        if (!airportsRes.ok) {
          throw new Error("Falha ao carregar aeroportos");
        }

        const airportsData: AirportOption[] = await airportsRes.json();

        console.log("AIRPORTS:", airportsData);

        const destinationsResults = await Promise.all(
          airportsData.map(async (airport) => {
            try {
              const res = await fetch(
                `${API_BASE_URL}/api/destinations/${encodeURIComponent(
                  airport.value
                )}`
              );

              if (!res.ok) return [];

              return (await res.json()) as string[];
            } catch {
              return [];
            }
          })
        );

        const uniqueDestinations = Array.from(
          new Set(destinationsResults.flat())
        )
          .filter(
            (item) =>
              !["faro", "lisbon", "seville"].includes(
                item.trim().toLowerCase()
              )
          )
          .sort((a, b) => a.localeCompare(b));

        if (!cancelled) {
          setAirports(airportsData);
          setAllDestinations(uniqueDestinations);
        }
      } catch (err) {
        if (!cancelled) {
          setAirports([]);
          setAllDestinations([]);
          setAirportsError(t("booking.errors.locations"));
          console.error("Erro ao carregar locais:", err);
        }
      } finally {
        if (!cancelled) setAirportsLoading(false);
      }
    }

    loadInitialData();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!formData.vehicleType) return;

    if (isVehicleDisabled(formData.vehicleType)) {
      setFormData((prev) => ({
        ...prev,
        vehicleType: "",
      }));
    }
  }, [totalPassengers, formData.vehicleType]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => {
      if (field === "country" && value !== "Other") {
        setCustomCountry("");
      }

      if (field === "pickup") {
        return {
          ...prev,
          pickup: value,
          destination: "",
        };
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleTripTypeChange = (value: TripType) => {
    setTripType(value);

    if (value === "oneWay") {
      setFormData((prev) => ({
        ...prev,
        returnDate: "",
        returnTime: "",
      }));
    }
  };

  const toDateInputValue = (d: Date | null) =>
    d ? d.toISOString().slice(0, 10) : "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const adults = Number(formData.adults || 0);
    const children = Number(formData.children || 0);
    const babies = Number(formData.babies || 0);
    const totalPassengers = adults + children + babies;

    if (totalPassengers > 8) {
      alert(t("booking.errors.maxPassengers"));
      return;
    }

    if (!formData.vehicleType) {
      alert(t("booking.errors.selectVehicle"));
      return;
    }

    const finalCountry =
      formData.country === "Other" ? customCountry.trim() : formData.country;

    if (formData.country === "Other" && !finalCountry) {
      alert(t("booking.errors.writeCountry"));
      return;
    }

    const payload: BookingPageData = {
      pickup: formData.pickup,
      pickupLabel: selectedPickupLabel,
      destination: formData.destination,
      tripType,
      tripDate,
      returnDate: formData.returnDate,
      returnTime: formData.returnTime,
      passengers: totalPassengers,
      adults,
      children,
      babies,
      time: formData.arrivalTime,
      resort: formData.resort,
      cabinLuggage: Number(formData.cabinLuggage || 0),
      checkedLuggage: Number(formData.checkedLuggage || 0),
      specialLuggage: formData.specialLuggage,
      vehicleType: formData.vehicleType,
      leadName: formData.leadName,
      email: formData.email,
      mobile: formData.mobile,
      country: finalCountry,
      foundUs: formData.foundUs,
      specialRequests: formData.specialRequests,
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/bookings/draft`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || t("booking.errors.createDraft"));
      }

      navigate(`/booking-summary/${data.bookingId}`);
    } catch (err) {
      console.error("Erro ao criar reserva temporária:", err);
      alert(t("booking.errors.summary"));
    }
  };

  return (
    <>
     <Helmet>
    <title>{t("seo.booking.title")}</title>

    <meta
      name="description"
      content={t("seo.booking.description")}
    />

    <link
      rel="canonical"
      href="https://francescotransfers.com/booking"
    />
  </Helmet>
    <section className="booking-page">
      <div className="booking-page-shell">
        <button
          type="button"
          className="booking-back-btn"
onClick={() => navigate(-1)}        >
          <ArrowLeft size={18} />
          {t("booking.back")}
        </button>

        <div className="booking-page-card">
          <div className="booking-page-card-header">
            <span className="booking-top-tag">{t("booking.header.tag")}</span>
            <h1>{t("booking.header.title")}</h1>
            <p>{t("booking.header.subtitle")}</p>
          </div>

          <form className="booking-page-form" onSubmit={handleSubmit}>
            <div className="booking-group">
              <span className="booking-section-label">{t("booking.tripType.label")}</span>

              <div className="trip-toggle">
                <button
                  type="button"
                  className={`trip-option ${
                    tripType === "oneWay" ? "active" : ""
                  }`}
                  onClick={() => handleTripTypeChange("oneWay")}
                >
                  {t("booking.tripType.oneWay")}
                </button>

                <button
                  type="button"
                  className={`trip-option ${
                    tripType === "roundTrip" ? "active" : ""
                  }`}
                  onClick={() => handleTripTypeChange("roundTrip")}
                >
                  {t("booking.tripType.roundTrip")}
                </button>
              </div>
            </div>

            <div className="booking-form-section">
              <div className="booking-form-section-title">
                <MapPin size={18} />
                <span>{t("booking.sections.transferDetails")}</span>
              </div>

              <div className="booking-page-row booking-page-grid-2">
                <div className="booking-field">
                  <label>{t("booking.fields.from")} *</label>
                  <select
                    value={formData.pickup}
                    onChange={(e) =>
                      handleInputChange("pickup", e.target.value)
                    }
                    required
                    disabled={airportsLoading}
                  >
                    <option value="">
                      {airportsLoading
                        ? t("booking.common.loadingLocations")
                        : airportsError
                        ? t("booking.common.unableToLoadLocations")
                        : t("booking.fields.chooseFrom")}
                    </option>

                    <optgroup label={t("booking.common.airports")}>
                      {airports.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </optgroup>

                    <optgroup label={t("booking.common.destinations")}>
                      {allDestinations.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </optgroup>
                  </select>

                  {airportsError && (
                    <small className="booking-field-error">
                      {airportsError}
                    </small>
                  )}
                </div>

                <div className="booking-field">
                  <label>{t("booking.fields.to")} *</label>

                  <select
                    value={formData.destination}
                    onChange={(e) =>
                      handleInputChange("destination", e.target.value)
                    }
                    required
                    disabled={airportsLoading}
                  >
                    <option value="">
                      {airportsLoading
                        ? t("booking.common.loadingLocations")
                        : airportsError
                        ? t("booking.common.unableToLoadLocations")
                        : t("booking.fields.chooseTo")}
                    </option>

                    <optgroup label={t("booking.common.airports")}>
                      {airports
                        .filter((item) => item.value !== formData.pickup)
                        .map((item) => (
                          <option
                            key={`to-airport-${item.value}`}
                            value={item.value}
                          >
                            {item.label}
                          </option>
                        ))}
                    </optgroup>

                    <optgroup label={t("booking.common.destinations")}>
                      {allDestinations
                        .filter((item) => item !== formData.pickup)
                        .map((item) => (
                          <option key={`to-destination-${item}`} value={item}>
                            {item}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                </div>
              </div>
            </div>

            <div className="booking-form-section">
              <div className="booking-form-section-title">
                <CalendarDays size={18} />
                <span>{t("booking.sections.dates")}</span>
              </div>

              <div className="booking-page-row booking-page-grid-2">
                <div className="booking-field">
                  <label>{t("booking.fields.checkInDate")} *</label>
                  <input
                    type="date"
                    value={toDateInputValue(tripDate)}
                    onChange={(e) =>
                      setTripDate(
                        e.target.value
                          ? new Date(`${e.target.value}T00:00:00`)
                          : null
                      )
                    }
                    required
                  />
                </div>

                <div
                  className={`booking-field ${
                    tripType === "oneWay" ? "is-disabled" : ""
                  }`}
                >
                  <label>
                    {t("booking.fields.checkOutDate")} {tripType === "roundTrip" ? "*" : ""}
                  </label>
                  <input
                    type="date"
                    value={formData.returnDate}
                    onChange={(e) =>
                      handleInputChange("returnDate", e.target.value)
                    }
                    disabled={tripType === "oneWay"}
                    required={tripType === "roundTrip"}
                  />
                </div>
              </div>

              <div className="booking-page-row booking-page-grid-2">
                <div className="booking-field">
                  <label>{t("booking.fields.arrivalTime")} *</label>
                  <input
                    type="time"
                    value={formData.arrivalTime}
                    onChange={(e) =>
                      handleInputChange("arrivalTime", e.target.value)
                    }
                    required
                  />
                </div>

                <div
                  className={`booking-field ${
                    tripType === "oneWay" ? "is-disabled" : ""
                  }`}
                >
                  <label>
                    {t("booking.tripType.roundTrip")} Time {tripType === "roundTrip" ? "*" : ""}
                  </label>
                  <input
                    type="time"
                    value={formData.returnTime}
                    onChange={(e) =>
                      handleInputChange("returnTime", e.target.value)
                    }
                    disabled={tripType === "oneWay"}
                    required={tripType === "roundTrip"}
                  />
                </div>
              </div>
            </div>

            <div className="booking-form-section">
              <div className="booking-form-section-title">
                <Users size={18} />
                <span>{t("booking.sections.passengers")}</span>
              </div>

              <div className="booking-page-row booking-page-grid-3">
                <div className="booking-field">
                  <label>{t("booking.fields.adults")} *</label>
                  <input
                    className="number-field"
                    type="number"
                    min={1}
                    step={1}
                    value={formData.adults}
                    onChange={(e) =>
                      handleInputChange("adults", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="booking-field">
                  <label>{t("booking.fields.children")}</label>
                  <input
                    className="number-field"
                    type="number"
                    min={0}
                    step={1}
                    value={formData.children}
                    onChange={(e) =>
                      handleInputChange("children", e.target.value)
                    }
                  />
                </div>

                <div className="booking-field">
                  <label>{t("booking.fields.babies")}</label>
                  <input
                    className="number-field"
                    type="number"
                    min={0}
                    step={1}
                    value={formData.babies}
                    onChange={(e) =>
                      handleInputChange("babies", e.target.value)
                    }
                  />
                </div>
              </div>

              {passengersError && (
                <small className="booking-field-error">
                  {passengersError}
                </small>
              )}
            </div>

            <div className="booking-form-section">
              <div className="booking-form-section-title">
                <CarFront size={18} />
                <span>{t("booking.sections.destinationResort")}</span>
              </div>

              <div className="booking-field">
                <label>{t("booking.fields.resort")} *</label>
                <input
                  type="text"
                  placeholder={t("booking.placeholders.mandatory")}
                  value={formData.resort}
                  onChange={(e) => handleInputChange("resort", e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="booking-form-section">
              <div className="booking-form-section-title">
                <CarFront size={18} />
                <span>{t("booking.sections.vehicle")}</span>
              </div>

              <div className="vehicle-options">
                {vehicles.map((vehicle) => {
                  const disabled = isVehicleDisabled(vehicle.value);

                  return (
                    <button
                      key={vehicle.value}
                      type="button"
                      disabled={disabled}
                      className={`vehicle-card ${
                        formData.vehicleType === vehicle.value ? "active" : ""
                      } ${disabled ? "disabled" : ""}`}
                      onClick={() => {
                        if (disabled) return;
                        handleInputChange("vehicleType", vehicle.value);
                      }}
                    >
                      <img src={vehicle.image} alt={vehicle.title} />

                      <div className="vehicle-card-content">
                        <strong>{vehicle.title}</strong>
                        <span>{vehicle.meta}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <select
                className="vehicle-hidden-select"
                value={formData.vehicleType}
                onChange={(e) =>
                  handleInputChange("vehicleType", e.target.value)
                }
                required
                aria-label={t("booking.fields.selectedVehicle")}
              >
                <option value="">{t("booking.fields.chooseVehicle")}</option>
                {vehicles.map((vehicle) => (
                  <option
                    key={vehicle.value}
                    value={vehicle.value}
                    disabled={isVehicleDisabled(vehicle.value)}
                  >
                    {vehicle.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="booking-form-section">
              <div className="booking-form-section-title">
                <User size={18} />
                <span>{t("booking.sections.personalInformation")}</span>
              </div>

              <div className="booking-page-row booking-page-grid-2">
                <div className="booking-field">
                  <label>{t("booking.fields.name")} *</label>
                  <div className="input-icon-wrap">
                    <User className="mini-icon" />
                    <input
                      type="text"
                      placeholder={t("booking.placeholders.firstName")}
                      value={formData.leadName}
                      onChange={(e) =>
                        handleInputChange("leadName", e.target.value)
                      }
                      required
                    />
                  </div>
                </div>

                <div className="booking-field">
                  <label>{t("booking.fields.email")} *</label>
                  <div className="input-icon-wrap">
                    <Mail className="mini-icon" />
                    <input
                      type="email"
                      placeholder={t("booking.placeholders.email")}
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="booking-page-row booking-page-grid-3">
                <div className="booking-field">
                  <label>{t("booking.fields.mobile")} *</label>
                  <div className="input-icon-wrap">
                    <Phone className="mini-icon" />
                    <input
                      type="tel"
                      placeholder={t("booking.placeholders.phone")}
                      value={formData.mobile}
                      onChange={(e) =>
                        handleInputChange("mobile", e.target.value)
                      }
                      required
                    />
                  </div>
                </div>

                <div className="booking-field">
                  <label>{t("booking.fields.country")} *</label>
                  <select
                    value={formData.country}
                    onChange={(e) =>
                      handleInputChange("country", e.target.value)
                    }
                    required
                  >
                    <option value="">{t("booking.common.select")}</option>
                    {countries.map((item) => (
                      <option key={item} value={item}>
                        {item === "Other" ? t("booking.common.other") : item}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="booking-field">
                  <label>{t("booking.fields.foundUs")}</label>
                  <select
                    value={formData.foundUs}
                    onChange={(e) =>
                      handleInputChange("foundUs", e.target.value)
                    }
                  >
                    <option value="">{t("booking.common.select")}</option>
                    {sources.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {formData.country === "Other" && (
                <div className="booking-page-row booking-page-grid-1">
                  <div className="booking-field">
                    <label>{t("booking.fields.writeCountry")} *</label>
                    <input
                      type="text"
                      placeholder={t("booking.placeholders.country")}
                      value={customCountry}
                      onChange={(e) => setCustomCountry(e.target.value)}
                      required
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="booking-form-section">
              <div className="booking-form-section-title">
                <MessageSquareMore size={18} />
                <span>{t("booking.sections.comments")}</span>
              </div>

              <div className="booking-field">
                <label>{t("booking.fields.additionalInformation")}</label>
                <textarea
                  placeholder={t("booking.placeholders.extraInformation")}
                  value={formData.specialRequests}
                  onChange={(e) =>
                    handleInputChange("specialRequests", e.target.value)
                  }
                />
              </div>
            </div>

            <Button
              type="submit"
              className="booking-submit-btn"
              disabled={!!passengersError}
            >
              {t("booking.continue")}
            </Button>
          </form>
        </div>
      </div>
    </section>
    </>
  );
}